'use client'

import ServiceService from '@/service/service.service'
import { ServiceObjectTypeResponse } from '@repo/shared/types/service.types'
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

export type ServiceCacheContextType = {
  services: ServiceObjectTypeResponse[]
  allServices: ServiceObjectTypeResponse[]
  loading: boolean
  error: Error | null
  refresh: () => Promise<void>
  getServiceForCode: (code: string) => ServiceObjectTypeResponse | undefined | null
  updateServiceById: (id: string, data: Partial<ServiceObjectTypeResponse>) => void
  addService: (data: ServiceObjectTypeResponse) => void
  deleteRestoreServiceById: (id: string, data: ServiceObjectTypeResponse) => void
}

const defaultValue: ServiceCacheContextType = {
  services: [],
  allServices: [],
  loading: true,
  error: null,
  refresh: async () => {},
  getServiceForCode: (code: string) => undefined,
  updateServiceById: (id: string, data: Partial<ServiceObjectTypeResponse>) => undefined,
  addService: (data: ServiceObjectTypeResponse) => undefined,
  deleteRestoreServiceById: (id: string, data: ServiceObjectTypeResponse) => undefined,
}
const ServiceCacheContext = createContext<ServiceCacheContextType>(defaultValue)

export const ServiceCacheProvider = ({ children }: { children: ReactNode }) => {
  const [allServices, setAllServices] = useState<ServiceObjectTypeResponse[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<Error | null>(null)

  // This is used to display only active services in the UI
  const services = useMemo(() => {
    return allServices.filter((service) => !service.deletedAt)
  }, [allServices])

  const fetchServices = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await ServiceService.getServices()
      setAllServices(response.data)
    } catch (err: any) {
      setError(err)
      setAllServices([])
    } finally {
      setLoading(false)
    }
  }, [])

  const getServiceForCode = useCallback(
    (code: string) => {
      return allServices.find((service) => service.code === code)
    },
    [allServices],
  )

  const updateServiceById = useCallback(
    async (id: string, data: Partial<ServiceObjectTypeResponse>) => {
      setLoading(true)
      setError(null)
      try {
        setAllServices((prev) =>
          prev.map((service) => (service.id === id ? { ...service, ...data } : service)),
        )
      } catch (err: any) {
        setError(err)
      } finally {
        setLoading(false)
      }
    },
    [],
  )

  const addService = useCallback(async (data: ServiceObjectTypeResponse) => {
    setLoading(true)
    setError(null)
    try {
      setAllServices((prev) => [data, ...prev])
    } catch (err: any) {
      setError(err)
    } finally {
      setLoading(false)
    }
  }, [])

  const deleteRestoreServiceById = useCallback(
    async (id: string, data: ServiceObjectTypeResponse) => {
      setLoading(true)
      setError(null)
      try {
        setAllServices((prev) =>
          prev.map((service) => (service.id === id ? { ...service, ...data } : service)),
        )
      } catch (err: any) {
        setError(err)
      } finally {
        setLoading(false)
      }
    },
    [],
  )

  return (
    <ServiceCacheContext.Provider
      value={{
        allServices,
        services,
        loading,
        error,
        refresh: fetchServices,
        getServiceForCode,
        updateServiceById,
        addService,
        deleteRestoreServiceById,
      }}
    >
      {children}
    </ServiceCacheContext.Provider>
  )
}

export const useServiceCache = () => {
  const context = useContext(ServiceCacheContext)
  const fetchedRef = useRef(false)

  // Lazy fetch: only fetch if not loaded and not already fetching
  // Move fetch logic to useEffect to avoid setState during render
  useEffect(() => {
    if (context.services.length === 0 && !context.loading && !fetchedRef.current) {
      fetchedRef.current = true
      context.refresh()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context.services.length, context.loading])

  return context
}
